export function formatAgeDisplay(age?: string) {
  const trimmedAge = age?.trim();
  if (!trimmedAge) {
    return '—';
  }

  const normalizedAge = trimmedAge.replace(/^0+(?=\d)/, '');
  if (!normalizedAge) {
    return '—';
  }

  if (/^\d+$/.test(normalizedAge)) {
    return `${normalizedAge}Y`;
  }

  return normalizedAge;
}

/** Backend enums: ROUTINE | URGENT | VIP | MLC (legacy EMERGENCY/STAT); empty → em dash in table. */
export function formatPriorityDisplay(raw?: string) {
  const trimmedPriority = raw?.trim();
  if (!trimmedPriority) {
    return '';
  }

  const priorityUpper = trimmedPriority.toUpperCase();
  if (priorityUpper === 'ROUTINE') {
    return 'Routine';
  }
  if (priorityUpper === 'URGENT') {
    return 'Urgent';
  }
  if (priorityUpper === 'VIP') {
    return 'VIP';
  }
  if (priorityUpper === 'MLC') {
    return 'MLC';
  }
  if (priorityUpper === 'EMERGENCY') {
    return 'Emergency';
  }
  if (priorityUpper === 'STAT') {
    return 'STAT';
  }

  return trimmedPriority.replace(/\w\S*/g, word => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
}

/** Figma: red pill (#DF2225) — URGENT, VIP, STAT, EMERGENCY. */
export function priorityUsesUrgentPill(raw?: string) {
  const priorityUpper = (raw || '').trim().toUpperCase();
  return (
    priorityUpper === 'URGENT' ||
    priorityUpper === 'VIP' ||
    priorityUpper === 'EMERGENCY' ||
    priorityUpper === 'STAT'
  );
}

/** Uppercase label on red pill (Figma). */
export function formatPriorityBadgeText(raw?: string) {
  const priorityUpper = (raw || '').trim().toUpperCase();
  if (priorityUpper === 'URGENT') {
    return 'URGENT';
  }
  if (priorityUpper === 'VIP') {
    return 'VIP';
  }
  if (priorityUpper === 'EMERGENCY') {
    return 'EMERGENCY';
  }
  if (priorityUpper === 'STAT') {
    return 'STAT';
  }
  return formatPriorityDisplay(raw).toUpperCase();
}

/** Normalize API strings (trim, lowercase, strip zero-width / odd spaces). */
export function normalizeReportStatusKey(status?: string) {
  if (status == null) {
    return '';
  }

  return String(status)
    .trim()
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\u00a0/g, ' ')
    .trim()
    .toLowerCase();
}

/** True when API sent an explicit Approved state (not "Awaiting approval" etc.). */
export function isApprovedReportStatus(status?: string) {
  const statusKey = normalizeReportStatusKey(status);
  return statusKey === 'approved';
}

/** Label inside the pill (title case for Approved so it stays readable on green). */
export function formatReportStatusLabel(raw?: string) {
  const trimmedStatus = (raw || '').trim();
  if (!trimmedStatus) {
    return '—';
  }
  if (isApprovedReportStatus(raw)) {
    return 'Approved';
  }
  return trimmedStatus;
}

/**
 * Figma: outlined report-status pill variant (border + tinted fill + status text color).
 * Modifier class only; pair with base `imaging-worklist-report-pill` on the element.
 */
export function reportStatusClass(status?: string) {
  const statusKey = normalizeReportStatusKey(status);
  if (!statusKey) {
    return 'imaging-worklist-report-pill--default';
  }
  if (isApprovedReportStatus(status)) {
    return 'imaging-worklist-report-pill--approved';
  }
  if (statusKey.includes('pending')) {
    return 'imaging-worklist-report-pill--pending';
  }
  if (statusKey.includes('draft')) {
    return 'imaging-worklist-report-pill--drafted';
  }
  if (statusKey.includes('await')) {
    return 'imaging-worklist-report-pill--awaiting';
  }
  if (statusKey.includes('reject')) {
    return 'imaging-worklist-report-pill--rejected';
  }
  return 'imaging-worklist-report-pill--default';
}

/** RIS / QIDO modality codes; `value` is sent to the API, `label` is shown in the UI. */
export const IMAGING_MODALITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'MR', label: 'MRI' },
  { value: 'CT', label: 'CT' },
  { value: 'XR', label: 'X-Ray' },
  { value: 'US', label: 'USG' },
];

const IMAGING_MODALITY_DISPLAY: Record<string, string> = Object.fromEntries(
  IMAGING_MODALITY_OPTIONS.map(o => [o.value, o.label])
);

const IMAGING_MODALITY_CODES = new Set(IMAGING_MODALITY_OPTIONS.map(o => o.value));

/** Table / badges: show friendly label for known imaging codes; otherwise show the token as-is. */
export function formatImagingModalityDisplay(code: string) {
  const key = code.trim().toUpperCase();
  return IMAGING_MODALITY_DISPLAY[key] ?? code;
}

export function isAllowedImagingModalityCode(code: string) {
  return IMAGING_MODALITY_CODES.has(code.trim().toUpperCase());
}

export function modalityTokens(modalities?: string) {
  const trimmedModalities = modalities?.trim();
  if (!trimmedModalities) {
    return [];
  }

  return trimmedModalities
    .split(/[/\\,]+/)
    .map(modalityToken => modalityToken.trim())
    .filter(Boolean);
}

export function formatAssignedHubFacilityDisplay(
  assignedHubFacilityId?: string,
  assignedHubFacilityName?: string,
  hubFacilityNameById?: Record<string, string>
) {
  const trimmedName = assignedHubFacilityName?.trim();
  if (trimmedName) {
    return trimmedName;
  }

  const trimmedId = assignedHubFacilityId?.trim();
  if (!trimmedId) {
    return 'Unassigned';
  }

  const resolvedName = hubFacilityNameById?.[trimmedId]?.trim();
  if (resolvedName) {
    return resolvedName;
  }

  return `Hub ${trimmedId}`;
}

export function formatSourceFacilityDisplay(sourceFacilityName?: string) {
  const trimmedName = sourceFacilityName?.trim();
  if (trimmedName) {
    return trimmedName;
  }

  return '—';
}

/** RIS visit appointment_type: WALK_IN | SCHEDULED */
export function formatAppointmentTypeDisplay(rawValue?: string) {
  if (!rawValue || !String(rawValue).trim()) {
    return '';
  }
  const normalized = String(rawValue).trim().toUpperCase();
  if (normalized === 'WALK_IN') {
    return 'Walk-in';
  }
  if (normalized === 'SCHEDULED') {
    return 'Scheduled';
  }
  return String(rawValue).trim();
}

/** AI badge visual variant (Sameeksha worklist design). */
export type AiResultBadgeVariant = 'normal' | 'abnormal' | 'pending' | 'failed';

export type AiResultBadge = {
  label: string;
  variant: AiResultBadgeVariant;
  tooltip: string;
  showSparkle: boolean;
};

/**
 * Resolve Imaging Worklist AI Result badge from study API fields.
 * Empty aiStatus → no badge (caller shows em dash).
 * Classification only when status is COMPLETED.
 * No confidence score.
 */
function formatInProgressAiLabel(status: string): string {
  if (status === 'PENDING') {
    return 'Pending';
  }
  if (status === 'QUEUED') {
    return 'Queued';
  }
  return 'Processing';
}

function formatFailedAiLabel(status: string): string {
  if (status === 'TIMEOUT') {
    return 'Timeout';
  }
  return 'Failed';
}

function buildAiResultBadge(
  label: string,
  variant: AiResultBadgeVariant,
  tooltipParts: string[],
  showSparkle: boolean
): AiResultBadge {
  return {
    label,
    variant,
    tooltip: tooltipParts.join('\n'),
    showSparkle,
  };
}

function appendTooltipLine(tooltipParts: string[], line: string): void {
  if (line) {
    tooltipParts.push(line);
  }
}

function resolveCompletedAiBadge(
  classification: string,
  modelVersion: string,
  tooltipParts: string[]
): AiResultBadge {
  if (classification === 'NORMAL') {
    appendTooltipLine(tooltipParts, 'AI status: Completed');
    appendTooltipLine(tooltipParts, 'Classification: Normal');
    if (modelVersion) {
      appendTooltipLine(tooltipParts, `Model: ${modelVersion}`);
    }
    return buildAiResultBadge('Normal', 'normal', tooltipParts, true);
  }

  if (classification === 'ABNORMAL') {
    appendTooltipLine(tooltipParts, 'AI status: Completed');
    appendTooltipLine(tooltipParts, 'Classification: Abnormal');
    if (modelVersion) {
      appendTooltipLine(tooltipParts, `Model: ${modelVersion}`);
    }
    return buildAiResultBadge('Abnormal', 'abnormal', tooltipParts, true);
  }

  appendTooltipLine(tooltipParts, 'AI status: Completed');
  appendTooltipLine(tooltipParts, 'Classification: Unknown');
  if (modelVersion) {
    appendTooltipLine(tooltipParts, `Model: ${modelVersion}`);
  }
  return buildAiResultBadge('Unknown', 'failed', tooltipParts, false);
}

function resolveInProgressAiBadge(
  status: string,
  modelVersion: string,
  tooltipParts: string[]
): AiResultBadge {
  const label = formatInProgressAiLabel(status);
  appendTooltipLine(tooltipParts, `AI status: ${label}`);
  if (modelVersion) {
    appendTooltipLine(tooltipParts, `Model: ${modelVersion}`);
  }
  return buildAiResultBadge(label, 'pending', tooltipParts, false);
}

function resolveFailedAiBadge(
  status: string,
  modelVersion: string,
  tooltipParts: string[]
): AiResultBadge {
  const label = formatFailedAiLabel(status);
  appendTooltipLine(tooltipParts, `AI status: ${label}`);
  if (modelVersion) {
    appendTooltipLine(tooltipParts, `Model: ${modelVersion}`);
  }
  return buildAiResultBadge(label, 'failed', tooltipParts, false);
}

export function resolveAiResultBadge(input: {
  aiStatus?: string;
  aiClassification?: string;
  aiModelVersion?: string;
}): AiResultBadge | null {
  const status = String(input.aiStatus || '')
    .trim()
    .toUpperCase();
  if (!status) {
    return null;
  }

  const classification = String(input.aiClassification || '')
    .trim()
    .toUpperCase();
  const modelVersion = String(input.aiModelVersion || '').trim();
  const tooltipParts: string[] = [];

  if (status === 'COMPLETED') {
    return resolveCompletedAiBadge(classification, modelVersion, tooltipParts);
  }

  if (status === 'PENDING' || status === 'QUEUED' || status === 'PROCESSING') {
    return resolveInProgressAiBadge(status, modelVersion, tooltipParts);
  }

  if (status === 'FAILED' || status === 'TIMEOUT') {
    return resolveFailedAiBadge(status, modelVersion, tooltipParts);
  }

  tooltipParts.push(`AI status: ${status}`);
  return {
    label: status.charAt(0) + status.slice(1).toLowerCase(),
    variant: 'failed',
    tooltip: tooltipParts.join('\n'),
    showSparkle: false,
  };
}

/** Modifier class for `imaging-worklist-ai-pill`. */
export function aiResultClass(variant: AiResultBadgeVariant) {
  return `imaging-worklist-ai-pill--${variant}`;
}

function pad2(value: number) {
  return String(value).padStart(2, '0');
}

function formatClock(hours: number, minutes: number) {
  return `${pad2(hours)}:${pad2(minutes)}`;
}

function formatDayMonthYear(year: number, monthIndex: number, day: number) {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${pad2(day)} ${months[monthIndex] ?? ''} ${year}`;
}

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const LOCAL_DATE_TIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/;

/** DICOM StudyDate (YYYYMMDD) + StudyTime (HHMMSS) for worklist columns. */
export function formatWorklistStudyDateTime(date?: string, time?: string) {
  const dateDigits = (date || '').replaceAll(/\D/g, '');
  if (dateDigits.length < 8) {
    return '—';
  }
  const year = Number(dateDigits.slice(0, 4));
  const monthIndex = Number(dateDigits.slice(4, 6)) - 1;
  const day = Number(dateDigits.slice(6, 8));
  const dateLabel = formatDayMonthYear(year, monthIndex, day);

  const timeDigits = (time || '').replaceAll(/\D/g, '');
  if (timeDigits.length >= 4) {
    return `${dateLabel}, ${formatClock(Number(timeDigits.slice(0, 2)), Number(timeDigits.slice(2, 4)))}`;
  }
  return dateLabel;
}

/** Report timestamps from API (ISO or date-like strings). Date-only values stay calendar-local. */
export function formatWorklistReportDateTime(value?: string) {
  const trimmed = value?.trim();
  if (!trimmed) {
    return '—';
  }

  const dateOnly = DATE_ONLY_RE.exec(trimmed);
  if (dateOnly) {
    return formatDayMonthYear(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }

  const localDateTime = LOCAL_DATE_TIME_RE.exec(trimmed);
  if (localDateTime) {
    return `${formatDayMonthYear(
      Number(localDateTime[1]),
      Number(localDateTime[2]) - 1,
      Number(localDateTime[3])
    )}, ${formatClock(Number(localDateTime[4]), Number(localDateTime[5]))}`;
  }

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime()) && /[tT]|\d{2}:\d{2}/.test(trimmed)) {
    return `${formatDayMonthYear(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate()
    )}, ${formatClock(parsed.getHours(), parsed.getMinutes())}`;
  }

  const dicomDigits = trimmed.replaceAll(/\D/g, '');
  if (dicomDigits.length >= 8) {
    return formatWorklistStudyDateTime(dicomDigits.slice(0, 8), dicomDigits.slice(8, 14));
  }
  return '—';
}
